import { toast } from 'react-toastify';
import { useBranding } from 'src/branding/BrandingContextProvider';
import {
  getAnnouncementBuildings,
  getAnnouncementLevels,
  getAnnouncementUnits,
  saveAnnouncement as saveAnnouncementRequest,
} from 'src/services/client';

export interface AnnouncementOption {
  id: string;
  name: string;
}

export type AnnouncementScopeType = 'Building' | 'Levels';

export interface FetchAnnouncementOptionsResult {
  ok: boolean;
  options: AnnouncementOption[];
}

export interface SaveAnnouncementResult {
  ok: boolean;
  data: any;
}

export interface UseAnnouncementAPIReturn {
  fetchBuildings: (
    propertyId: string
  ) => Promise<FetchAnnouncementOptionsResult>;
  fetchLevels: (propertyId: string) => Promise<FetchAnnouncementOptionsResult>;
  fetchUnits: (
    propertyId: string,
    type: AnnouncementScopeType,
    scopeIds: string[]
  ) => Promise<FetchAnnouncementOptionsResult>;
  saveAnnouncement: (params: any) => Promise<SaveAnnouncementResult>;
}

const isOkResponse = (response: any): boolean =>
  response.ok || response.status === 200;

export const useAnnouncementAPI = (): UseAnnouncementAPIReturn => {
  const { branding } = useBranding();

  const suitePlural: string = `${(branding?.text?.suite || 'Suite').toLowerCase()}s`;

  const fetchBuildings = async (
    propertyId: string
  ): Promise<FetchAnnouncementOptionsResult> => {
    try {
      const response: any = await getAnnouncementBuildings(propertyId);

      if (!isOkResponse(response)) {
        throw new Error(`Failed to fetch buildings: ${response.status}`);
      }

      const payload: any[] = await response.json();
      const options: AnnouncementOption[] = payload
        .filter((item: any) => item.building !== null)
        .map((item: any) => ({ id: item.id, name: item.building }));

      return { ok: true, options };
    } catch (err: any) {
      console.error('useAnnouncementAPI fetchBuildings failed:', err);
      toast.error(`🚫 ${err?.message || 'Failed to fetch buildings'}`);

      return { ok: false, options: [] };
    }
  };

  const fetchLevels = async (
    propertyId: string
  ): Promise<FetchAnnouncementOptionsResult> => {
    try {
      const response: any = await getAnnouncementLevels(propertyId);

      if (!isOkResponse(response)) {
        throw new Error(`Failed to fetch levels: ${response.status}`);
      }

      const payload: any[] = await response.json();
      const options: AnnouncementOption[] = payload
        .filter((item: any) => item.level !== null)
        .map((item: any) => ({ id: item.id, name: item.level }));

      return { ok: true, options };
    } catch (err: any) {
      console.error('useAnnouncementAPI fetchLevels failed:', err);
      toast.error(`🚫 ${err?.message || 'Failed to fetch levels'}`);

      return { ok: false, options: [] };
    }
  };

  const fetchUnits = async (
    propertyId: string,
    type: AnnouncementScopeType,
    scopeIds: string[]
  ): Promise<FetchAnnouncementOptionsResult> => {
    const requestBody = {
      PropertyId: propertyId,
      Type: type,
      BuildingLevels: scopeIds.map((id: string) => ({ Id: id })),
    };

    try {
      const response: any = await getAnnouncementUnits(requestBody);

      if (!isOkResponse(response)) {
        throw new Error(`Failed to fetch ${suitePlural}: ${response.status}`);
      }

      const payload: any[] = await response.json();
      const options: AnnouncementOption[] = payload
        .filter((item: any) => item.name !== null)
        .map((item: any) => ({ id: item.unitId, name: item.name }));

      return { ok: true, options };
    } catch (err: any) {
      console.error('useAnnouncementAPI fetchUnits failed:', err);
      toast.error(`🚫 ${err?.message || `Failed to fetch ${suitePlural}`}`);

      return { ok: false, options: [] };
    }
  };

  const saveAnnouncement = async (
    params: any
  ): Promise<SaveAnnouncementResult> => {
    try {
      const response: any = await saveAnnouncementRequest(params);

      if (!isOkResponse(response)) {
        throw new Error(`Failed to save announcement: ${response.status}`);
      }

      const payload: any = await response.json();

      if (payload.statusCode !== 200) {
        throw new Error(`Failed to save announcement: ${payload.statusCode}`);
      }

      return { ok: true, data: payload.data };
    } catch (err: any) {
      console.error('useAnnouncementAPI saveAnnouncement failed:', err);
      toast.error(
        ' 🚫 Something went wrong with adding a announcement, please try again.',
        { autoClose: 12000, position: 'top-center' }
      );

      return { ok: false, data: null };
    }
  };

  return { fetchBuildings, fetchLevels, fetchUnits, saveAnnouncement };
};
